import styled, { css } from "styled-components";

// ======================
// Funções auxiliares
// ======================

function getGradient(color: string): string {
  switch (color) {
    case "from-blue-500 to-blue-600":
      return "linear-gradient(to right, #3B82F6, #2563EB)";
    case "from-emerald-500 to-emerald-600":
      return "linear-gradient(to right, #10B981, #059669)";
    case "from-purple-500 to-purple-600":
      return "linear-gradient(to right, #8B5CF6, #7C3AED)";
    default:
      return "linear-gradient(to right, #3B82F6, #2563EB)";
  }
}

function getPositionGradient(position: number): string {
  switch (position) {
    case 1:
    case 2:
    case 3:
      return "linear-gradient(to bottom right, #3B82F6, #2563EB)";
    default:
      return "linear-gradient(to bottom right, #E5E7EB, #D1D5DB)";
  }
}

// ======================
// Layout Geral
// ======================

export const Content = styled.div`
  width: 100%;
  background-color: #f5f5f5;
`;

export const Container = styled.div`
  background-color: #ffffff;
  border-radius: 16px;
  width: 100%;
  /* Altura máxima para scroll interno */

  padding: 8px 16px 16px 16px;
  box-shadow: 0px 4px 4px 0px #00000040;
`;

// ======================
// Header
// ======================

export const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 0;
`;

export const HeaderTitle = styled.h1`
  font-family: Inter;
  font-weight: 600;
  font-size: 24px;
  line-height: 29.05px;
  letter-spacing: -1%;
  background: linear-gradient(to right, #1f2937, #4b5563);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

// ======================
// Regionais (grid cards)
// ======================

export const RegionalsGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
  overflow-y: auto;
  max-height: 281px;
  overflow-x: hidden;

  /* Responsivo */
  @media (min-width: 768px) {
    grid-template-columns: repeat(3, 1fr);
  }
`;

/** Card de cada Regional */
interface RegionalCardProps {
  isActive?: boolean;
}
export const RegionalCard = styled.div<RegionalCardProps>`
  border: 1px solid #e2e2e2;
  border-radius: 12px;
  overflow: hidden;

  /* Sombra padrão no card */
  box-shadow: 0 2px 5px rgba(0, 0, 0, 0.06);
`;

interface RegionalCardHeaderProps {
  color: string;
}
export const RegionalCardHeader = styled.div<RegionalCardHeaderProps>`
  padding: 24px;
  /* Gradiente dinâmico */
  background: ${({ color }) => getGradient(color)};
  color: #333333;
`;

export const RegionalHeaderTitle = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 16px;
  font-family: Inter;
  font-weight: 700;
  font-size: 18px;
  letter-spacing: -1%;
`;

export const RegionalStats = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 8px;
`;

export const StatsItem = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -1%;
`;

export const RegionalCardBody = styled.div`
  padding: 16px;
`;

export const Top3Item = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;

  &:last-child {
    margin-bottom: 0;
  }
`;

interface PositionBadgeProps {
  position: number;
}
export const PositionBadge = styled.div<PositionBadgeProps>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: bold;
  color: #333333;
  display: flex;
  align-items: center;
  justify-content: center;
  background: ${({ position }) => getPositionGradient(position)};
`;

export const Avatar = styled.img`
  width: 32px;
  height: 32px;
  border-radius: 8px;
  object-fit: cover;
  flex-shrink: 0;
`;

export const RankInfo = styled.div`
  flex: 1;

  p {
    font-family: Inter;
    font-weight: 700;
    font-size: 14px;
    letter-spacing: -1%;
    margin: 0;
  }

  span {
    font-family: Inter;
    font-weight: 400;
    font-size: 14px;
    letter-spacing: -1%;
  }
`;

export const PointsInfo = styled.div`
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  letter-spacing: -1%;
  color: #4b5563;
`;

// ======================
// Botão fixo na parte de baixo
// ======================

/**
 * Wrapper "gruda" no final do container ao rolar,
 * graças ao position: sticky.
 */
export const BottomButtonWrapper = styled.div`
  position: sticky;
  bottom: 0;
  background-color: #ffffff;
  padding-top: 8px;
  display: flex;
  justify-content: center;
`;

/** Botão em si */
export const BottomButton = styled.a`
  text-align: center;
  width: 100%;
  border: 1px solid #1a49cc;
  background-color: #ffffff;
  color: #1a49cc;
  font-family: Inter;
  font-weight: 500;
  font-size: 14px;
  padding: 8px 16px;
  border-radius: 8px;
  cursor: pointer;
  text-decoration: none;

  &:hover {
    background-color: #f3f6ff;
  }
`;

export const Title = styled.h1`
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
  color: #00000099;
`;

export const Description = styled.p`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
  color: #00000099;
`;

export const HeaderWrapper = styled.div`
  margin-bottom: 16px;
`;

export const TeamGridWrapper = styled.div`
  max-height: 350px;
  overflow-y: auto;
  margin-top: 8px;

  &::-webkit-scrollbar-thumb {
    background-color: #a3a3a6;
    border-radius: 8px;
  }
  &::-webkit-scrollbar {
    height: 6px;
    width: 6px;
    background-color: transparent;
  }
  &::-webkit-scrollbar-track {
    border-radius: 8px;
  }
`;

export const ManagerListWrapper = styled.div`
  flex: 1;
  margin-bottom: 24px;
`;

export const ContainerFooter = styled.div`
  display: flex;
  justify-content: center;
`;

export const ColumnsWrapper = styled.div`
  display: flex;
  gap: 16px;

  /* Ajuste em telas grandes/pequenas conforme desejado */
  @media (max-width: 3000px) {
    flex-direction: column;
  }
`;

export const Column = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const ManagerCard = styled.div`
  border-radius: 16px;
  display: flex;
  flex-direction: column;
`;

export const ManagerButton: any = styled.button`
  width: 100%;
  text-align: left;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: ${(props: any) => props?.background};
  border-radius: 20px;
  border: 2px solid ${(props: any) => props?.border};
  overflow: hidden;
  cursor: pointer;
`;

export const ManagerInfo = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

export const ManagerAvatar = styled.img`
  width: 60px;
  height: 60px;
  border-radius: 50%;
  object-fit: cover;
  margin-left: 8px;
`;

export const ManagerDetailsWrapper = styled.div`
  display: flex;
  flex-direction: column;
`;

/**
 * Truncamos o nome/apelido do manager.
 */
export const ManagerName = styled.h3`
  color: #333333;
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 24px;
  line-height: 29px;
  letter-spacing: -0.01em;

  max-width: 300px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const ManagerRole = styled.p`
  font-size: 14px;
  color: #333333;
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  line-height: 29px;
  letter-spacing: -0.01em;

  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

/**
 * Aqui está o "segredo" da animação de abrir/fechar.
 * Sempre no DOM, mas com max-height e padding controlados por 'isOpen'.
 */
export const ManagerDetails = styled.div<{ isOpen: boolean }>`
  overflow: hidden;
  transition: max-height 0.3s ease, padding 0.3s ease;

  /* Ajuste a altura máxima de acordo com a possível altura do conteúdo */
  max-height: ${({ isOpen }) => (isOpen ? "800px" : "0")};

  /* Quando fechado, podemos remover o padding para "sumir" visualmente */
  padding: ${({ isOpen }) => (isOpen ? "16px" : "0 16px")};
`;

export const ContactInfo = styled.div`
  margin-bottom: 16px;
  display: flex;
  gap: 16px;
  color: #666;
`;

export const ContactItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;

  :hover {
    opacity: 0.8;
  }
`;

export const ContactInfoText = styled.p`
  font-family: "Roboto";
  font-style: normal;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  letter-spacing: -0.01em;
  color: #333333;
  max-width: 250px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TeamHeading = styled.h4`
  font-size: 24px;
  color: #333;
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  line-height: 29px;
  letter-spacing: -0.01em;
  margin-bottom: 16px;
`;

export const TeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 12px;

  @media (min-width: 640px) {
    grid-template-columns: repeat(2, 1fr);
  }
`;

export const TeamMemberCard = styled.div`
  display: grid;
  align-items: center;
  gap: 16px;
  background-color: #f9f9f9;
  padding: 12px;
  border-radius: 8px;
  grid-template-columns: repeat(2, 1fr);
`;

export const TeamMemberAvatar = styled.img`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  object-fit: cover;
  border: 2px solid #808080;
`;

export const TeamMemberInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

export const TeamMemberName = styled.h5`
  color: #333;
  font-family: "Inter";
  font-style: normal;
  font-weight: 600;
  font-size: 16px;
  line-height: 29px;
  letter-spacing: -0.01em;

  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

export const TeamMemberRole = styled.p`
  font-size: 12px;
  color: #666;
  font-family: "Inter";
  font-style: normal;
  font-weight: 400;
  letter-spacing: -0.01em;

  max-width: 150px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;
